import React, { useEffect, useState } from "react";
import { View, StyleSheet, Pressable, Platform } from "react-native";
import useResponsiveScreen from "../../../hooks/useResponsiveScreen";
import CustomButton from "../../common/general/CustomButton/CustomButton";
import { PlusCircle } from "../../svgIcons";
import CustomText from "../../common/general/CustomText/CustomText";
import { ListItem } from "@rneui/themed";
import CustomSpacing from "../../common/layout/CustomSpacing/CustomSpacing";
import CustomCourseworkMenuItem from "./CustomCourseworkMenuItem";
import { sortResourceItemsOfModules as sortItemsOfModulesAndResources } from "../../../utils/sortResourceItemsOfModules";
import { CaretDown } from "@phosphor-icons/react";
import DraggableFlatList, { ScaleDecorator } from "react-native-draggable-flatlist";

const BUTTON_HEIGHT = 36;

function CustomCourseWorkContentMenu({
  data,
  onSelectModule,
  onSelectActivity,
  selectedModule,
  selectedActivity,
  onAddModule,
  onModuleDragEnd,
}) {
  const { isXLarge, isMedium, hp } = useResponsiveScreen();
  const [expandedModule, setExpandedModule] = useState(null);
  const [modules, setModules] = useState([]);

  useEffect(() => {
    if (data?.modules) {
      const modules = data.modules;
      setModules(modules);

      if (!expandedModule && modules.length > 0) {
        const firstModule = modules[0];
        setExpandedModule(firstModule.id);
        onSelectModule(firstModule);
      }
    }
  }, [data?.modules]);

  const handleSelectModule = (module) => {
    const isExpanded = expandedModule === module.id;

    if (selectedActivity && selectedModule?.id === module.id) {
      setExpandedModule(module.id);
      onSelectModule(module);
      return;
    }

    setExpandedModule(isExpanded ? null : module.id);
    if (!isExpanded) {
      onSelectModule(module);
    }
  };

  const handleItemPress = (item) => {
    onSelectActivity(item);
  };

  const handleModuleDragEnd = (data) => {
    setModules(data);
    if (onModuleDragEnd) {
      onModuleDragEnd(data);
    }
  };

  const getModuleItems = (module) => {
    // Combine aiTutors and axioResources
    const items = [
      ...(module?.aiTutors || []).map((item) => ({
        ...item,
        type: "Interactive Learning Experience",
      })),
      ...(module.axioResources || []).map((item) => ({
        ...item,
        title: item.name, // Map name to title for consistency
        type: "Resource",
      })),
      ...(module?.pages || []).map((item) => ({
        ...item,
        type: "Page",
      })),
      ...(module?.assignments || []).map((item) => ({
        ...item,
        title: item.name,
        type: "Assignment",
      })),
      ...(module?.reviews || []).map((item) => ({
        ...item,
        title: `Review for ${item.hasReviewFor?.moduleHasAssessment?.title || "Unknown Module"}'s Assessment`,
        type: "Review",
      })),
      ...(module?.assessment?.id
        ? [
            {
              ...module.assessment,
              title: "Lesson Assessment",
              type: "Assessment",
            },
          ]
        : []),
      ...(module?.projects || []).map((item) => ({
        ...item,
        title: item.name || item.title, // Map name to title for consistency
        type: "Project",
      })),
    ];

    return sortItemsOfModulesAndResources(items, module.resourceOrder);
  };

  const renderModule = ({ item: module, drag, isActive }) => {
    const items = getModuleItems(module);
    const isModuleSelected = selectedModule?.id === module.id || items.some((item) => item.id === selectedActivity?.id);

    const handleDragStart = (e) => {
      e.preventDefault();
      if (expandedModule === module.id) {
        setExpandedModule(null);
      }
      drag(e);
    };

    return (
      <ScaleDecorator>
        <View
          testID={`module-row-${module.id}`}
          style={[styles.moduleContainer, isActive && styles.draggingModule]}
          accessibilityRole="menu">
          <ListItem.Accordion
            role="button"
            aria-label={`${module.title}`}
            accessibilityLabel={`${module.title}`}
            isExpanded={expandedModule === module.id}
            onPress={() => handleSelectModule(module)}
            noIcon={true}
            content={
              <>
                <View style={styles.moduleTitleContainer}>
                  {isModuleSelected && (
                    <>
                      <View style={styles.iconContainer}>
                        <CaretDown size={16} color="#667085" weight="bold" />
                      </View>
                      <CustomSpacing type="horizontal" size="s" />
                    </>
                  )}
                  <CustomText
                    useTranslationText={false}
                    text={module.title}
                    weight={isModuleSelected ? "ultraBold" : "medium"}
                    style={[styles.moduleText, isModuleSelected && styles.selectedModuleText]}
                  />
                </View>
                {modules.length > 1 && isModuleSelected && (
                  <Pressable
                    role="button"
                    accessibilityLabel="Drag module"
                    onPointerDown={handleDragStart}
                    style={[styles.dragHandle, { borderTopRightRadius: 10, borderBottomRightRadius: 10 }]}>
                    <View style={styles.dragDots}>
                      <View style={styles.dot} />
                      <View style={styles.dot} />
                    </View>
                    <View style={styles.dragDots}>
                      <View style={styles.dot} />
                      <View style={styles.dot} />
                    </View>
                  </Pressable>
                )}
              </>
            }
            containerStyle={[styles.accordionContainer, isModuleSelected && styles.selectedAccordionContainer]}>
            {items.map((item, index) => {
              const isActivitySelected = selectedActivity?.id === item.id;
              return (
                <CustomCourseworkMenuItem
                  key={item.id}
                  onItemPress={() => handleItemPress(item)}
                  name={item.title}
                  type={item.type}
                  isSelected={isActivitySelected}
                  isLast={index === items.length - 1}
                  isFirst={index === 0}
                />
              );
            })}
          </ListItem.Accordion>
        </View>
      </ScaleDecorator>
    );
  };

  return (
    <View style={[styles.container, { width: isXLarge ? 360 : isMedium ? 240 : hp(100) }]} role="menu">
      <CustomSpacing type="vertical" size="xxl" />
      <View style={{ paddingHorizontal: 20 }}>
        <View style={styles.menuContainer}>
          <CustomText role="header" aria-level="3" text="Modules" weight="ultraBold" style={{ color: "#121212" }} />
          <CustomButton
            text="Add Module"
            leftIcon={<PlusCircle />}
            styleType="primaryTransparentStroke"
            size="s"
            onPress={onAddModule}
            aria-label="Add Module"
            textStyle={{ fontSize: 14, fontWeight: 700 }}
            style={styles.generalButtonStyle}
          />
        </View>
      </View>

      <View style={styles.scrollView}>
        {modules.length > 0 ? (
          <DraggableFlatList
            testID="modules-draggable-list"
            data={modules}
            onDragEnd={({ data }) => handleModuleDragEnd(data)}
            keyExtractor={(item) => item.id}
            renderItem={renderModule}
            dragItemOverflow={true}
            activationDistance={5}
            autoscrollSpeed={50}
            containerStyle={{ flex: 1 }}
            showsVerticalScrollIndicator={true}
          />
        ) : (
          <View style={styles.noModulesContainer}>
            <CustomText
              text="To get started, click the Add Module button above"
              size="m"
              style={styles.noModulesText}
              useTranslationText={false}
            />
          </View>
        )}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: "#EAECF0",
    backgroundColor: "#fff",
  },
  scrollView: {
    height: 20,
    paddingTop: 12,
    flex: 1,
  },
  menuContainer: {
    paddingVertical: 10,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    backgroundColor: "#fff",
    borderBottomColor: "#D0D5DD",
    borderBottomWidth: 1,
  },
  moduleContainer: {
    position: "relative",
    marginBottom: 12,
    paddingHorizontal: 20,
  },
  accordionContainer: {
    paddingHorizontal: 12,
    paddingVertical: 0,
  },
  moduleTitleContainer: {
    flexDirection: "row",
    alignItems: "center",
    flex: 1,
    minWidth: 0,
  },
  iconContainer: {
    width: 16,
    height: 16,
    justifyContent: "center",
    alignItems: "center",
  },
  moduleText: {
    color: "#182230",
    flexShrink: 1,
    minWidth: 0,
  },
  selectedModuleText: {
    color: "#182230",
  },
  selectedAccordionContainer: {
    backgroundColor: "#F2F5FF",
    paddingHorizontal: 12,
    paddingVertical: 14,
    borderRadius: 10,
  },
  noModulesContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#F2F5FF",
    borderRadius: 10,
    padding: 40,
  },
  noModulesText: {
    color: "#344054",
  },
  generalButtonStyle: {
    padding: 0,
    paddingHorizontal: 12,
    paddingVertical: 8,
    height: BUTTON_HEIGHT,
    borderColor: "#EAECF0",
  },
  dragHandle: {
    position: "absolute",
    right: 0,
    top: 0,
    bottom: 0,
    flexDirection: "row",
    width: 20,
    justifyContent: "center",
    gap: 2,
    alignItems: "center",
    marginLeft: 8,
    backgroundColor: "#ccc",
  },
  dragDots: {
    justifyContent: "center",
    height: 12,
    gap: 2,
  },
  dot: {
    width: 3,
    height: 3,
    borderRadius: 1.5,
    backgroundColor: "#9CA3AF",
    marginVertical: 1,
  },
  draggingModule: {
    opacity: 0.9,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 1,
    ...(Platform.OS === "web" && {
      cursor: "grabbing" as any,
    }),
  },
});

export default CustomCourseWorkContentMenu;
